import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProjects } from '@/context/ProjectContext';
import type { Artifact, Deployment } from '@/types';
import { cn } from '@/lib/utils';

type Tab = 'preview' | 'artifacts' | 'deployments' | 'settings';
type DeviceSize = 'mobile' | 'tablet' | 'desktop';

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    activeProject,
    artifacts,
    deployments,
    isLoadingProject,
    error,
    selectProject,
    deleteProject,
    deployProject,
    clearActiveProject,
  } = useProjects();

  const [activeTab, setActiveTab] = useState<Tab>('preview');
  const [deviceSize, setDeviceSize] = useState<DeviceSize>('desktop');
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (id) {
      selectProject(id);
    }
    return () => {
      clearActiveProject();
    };
  }, [id, selectProject, clearActiveProject]);

  const handleDeploy = async (environment: 'development' | 'staging' | 'production') => {
    if (!activeProject) return;
    setIsDeploying(true);
    setDeployError(null);
    try {
      await deployProject(activeProject.id, environment);
    } catch (err) {
      setDeployError(err instanceof Error ? err.message : 'Deployment failed');
    } finally {
      setIsDeploying(false);
    }
  };

  const handleDelete = async () => {
    if (!activeProject) return;
    await deleteProject(activeProject.id);
    navigate('/projects');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'deployed':
      case 'live':
        return 'bg-green-500/20 text-green-400';
      case 'active':
      case 'building':
      case 'deploying':
        return 'bg-blue-500/20 text-blue-400';
      case 'draft':
      case 'pending':
        return 'bg-zinc-500/20 text-zinc-400';
      case 'archived':
      case 'failed':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getDeviceWidth = () => {
    switch (deviceSize) {
      case 'mobile':
        return 'max-w-[375px]';
      case 'tablet':
        return 'max-w-[768px]';
      default:
        return 'max-w-full';
    }
  };

  if (isLoadingProject) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  if (error || !activeProject) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
          <span className="material-icons text-3xl text-red-400">error_outline</span>
        </div>
        <h2 className="text-xl font-semibold text-zinc-200 mb-2">
          {error || 'Project not found'}
        </h2>
        <button
          onClick={() => navigate('/projects')}
          className="px-4 py-2 bg-zinc-700 text-zinc-200 rounded-lg hover:bg-zinc-600 transition-colors"
        >
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/projects')}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <span className="material-icons text-zinc-400">arrow_back</span>
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-zinc-100">{activeProject.name}</h1>
              <span className={cn('px-2 py-0.5 text-xs rounded-full', getStatusColor(activeProject.status))}>
                {activeProject.status}
              </span>
            </div>
            {activeProject.description && (
              <p className="text-sm text-zinc-400">{activeProject.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {deployError && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm">
              <span className="material-icons text-sm">error</span>
              {deployError}
              <button
                onClick={() => setDeployError(null)}
                className="ml-1 hover:text-red-300"
              >
                <span className="material-icons text-sm">close</span>
              </button>
            </div>
          )}
          {activeProject.preview_url && (
            <a
              href={activeProject.preview_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-zinc-700 text-zinc-200 rounded-lg hover:bg-zinc-600 transition-colors"
            >
              <span className="material-icons text-sm">open_in_new</span>
              Preview
            </a>
          )}
          <button
            onClick={() => handleDeploy('production')}
            disabled={isDeploying}
            className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50"
          >
            {isDeploying ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Deploying...
              </>
            ) : (
              <>
                <span className="material-icons text-sm">rocket_launch</span>
                Deploy
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-800">
        {(['preview', 'artifacts', 'deployments', 'settings'] as Tab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-4 py-3 text-sm font-medium transition-colors capitalize border-b-2 -mb-px',
              activeTab === tab
                ? 'text-brand border-brand'
                : 'text-zinc-400 border-transparent hover:text-zinc-200'
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'preview' && (
          <div className="h-full flex flex-col">
            {/* Device Toggle */}
            <div className="flex items-center justify-center gap-2 p-4 bg-zinc-900">
              {(['mobile', 'tablet', 'desktop'] as DeviceSize[]).map((size) => (
                <button
                  key={size}
                  onClick={() => setDeviceSize(size)}
                  className={cn(
                    'p-2 rounded-lg transition-colors',
                    deviceSize === size
                      ? 'bg-brand text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  )}
                  title={size.charAt(0).toUpperCase() + size.slice(1)}
                >
                  <span className="material-icons">
                    {size === 'mobile' ? 'smartphone' : size === 'tablet' ? 'tablet' : 'desktop_windows'}
                  </span>
                </button>
              ))}
            </div>

            {/* Preview iframe */}
            <div className="flex-1 bg-zinc-900 flex items-start justify-center p-4 overflow-auto">
              <div className={cn('w-full h-full bg-white rounded-lg overflow-hidden shadow-2xl', getDeviceWidth())}>
                {activeProject.preview_url ? (
                  <iframe
                    src={activeProject.preview_url}
                    className="w-full h-full border-0"
                    title="Site Preview"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-zinc-100">
                    <div className="text-center">
                      <span className="material-icons text-6xl text-zinc-400 mb-4">preview</span>
                      <p className="text-zinc-500">Deploy your project to see a preview</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'artifacts' && (
          <div className="p-6">
            {artifacts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
                  <span className="material-icons text-3xl text-zinc-500">description</span>
                </div>
                <h2 className="text-xl font-semibold text-zinc-200 mb-2">No artifacts yet</h2>
                <p className="text-zinc-400 text-center max-w-md">
                  Generated files will appear here as you build your site.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {artifacts.map((artifact) => (
                  <ArtifactRow key={artifact.id} artifact={artifact} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'deployments' && (
          <div className="p-6">
            {deployments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
                  <span className="material-icons text-3xl text-zinc-500">cloud_upload</span>
                </div>
                <h2 className="text-xl font-semibold text-zinc-200 mb-2">No deployments yet</h2>
                <p className="text-zinc-400 text-center max-w-md">
                  Deploy your project to make it live.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {deployments.map((deployment) => (
                  <DeploymentCard key={deployment.id} deployment={deployment} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="p-6 max-w-2xl">
            <div className="space-y-6">
              {/* Project Info */}
              <div className="bg-elevated rounded-lg border border-zinc-800 p-6">
                <h3 className="text-lg font-semibold text-zinc-100 mb-4">Project Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1">Name</label>
                    <p className="text-zinc-100">{activeProject.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1">Slug</label>
                    <p className="text-zinc-100 font-mono">{activeProject.slug}</p>
                  </div>
                  {activeProject.recipe_name && (
                    <div>
                      <label className="block text-sm font-medium text-zinc-400 mb-1">Recipe</label>
                      <p className="text-zinc-100">{activeProject.recipe_name}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1">Created</label>
                    <p className="text-zinc-100">
                      {new Date(activeProject.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Git Info */}
              {activeProject.git_repo_url && (
                <div className="bg-elevated rounded-lg border border-zinc-800 p-6">
                  <h3 className="text-lg font-semibold text-zinc-100 mb-4">Git Repository</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-400 mb-1">Repository URL</label>
                      <p className="text-zinc-100 font-mono text-sm break-all">{activeProject.git_repo_url}</p>
                    </div>
                    {activeProject.git_last_commit_sha && (
                      <div>
                        <label className="block text-sm font-medium text-zinc-400 mb-1">Last Commit</label>
                        <p className="text-zinc-100 font-mono text-sm">{activeProject.git_last_commit_sha.slice(0, 8)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* URLs */}
              {(activeProject.preview_url || activeProject.production_url) && (
                <div className="bg-elevated rounded-lg border border-zinc-800 p-6">
                  <h3 className="text-lg font-semibold text-zinc-100 mb-4">URLs</h3>
                  <div className="space-y-4">
                    {activeProject.preview_url && (
                      <div>
                        <label className="block text-sm font-medium text-zinc-400 mb-1">Preview URL</label>
                        <a
                          href={activeProject.preview_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand hover:underline"
                        >
                          {activeProject.preview_url}
                        </a>
                      </div>
                    )}
                    {activeProject.production_url && (
                      <div>
                        <label className="block text-sm font-medium text-zinc-400 mb-1">Production URL</label>
                        <a
                          href={activeProject.production_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand hover:underline"
                        >
                          {activeProject.production_url}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Danger Zone */}
              <div className="bg-elevated rounded-lg border border-red-500/30 p-6">
                <h3 className="text-lg font-semibold text-red-400 mb-4">Danger Zone</h3>
                <p className="text-zinc-400 mb-4">
                  Deleting a project is permanent and cannot be undone.
                </p>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                >
                  Delete Project
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="bg-elevated rounded-lg border border-zinc-700 max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-zinc-100 mb-2">Delete Project?</h2>
            <p className="text-zinc-400 mb-6">
              Are you sure you want to delete "{activeProject.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-zinc-700 text-zinc-200 rounded-lg hover:bg-zinc-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ArtifactRow({ artifact }: { artifact: Artifact }) {
  const getFileIcon = (fileType: string | null) => {
    switch (fileType) {
      case 'tsx':
      case 'jsx':
        return 'code';
      case 'css':
        return 'palette';
      case 'html':
        return 'html';
      case 'json':
        return 'data_object';
      case 'png':
      case 'jpg':
      case 'svg':
        return 'image';
      default:
        return 'description';
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="flex items-center gap-4 p-3 bg-elevated rounded-lg border border-zinc-800 hover:border-zinc-700 transition-colors">
      <span className="material-icons text-zinc-500">{getFileIcon(artifact.file_type)}</span>
      <div className="flex-1 min-w-0">
        <p className="text-zinc-100 font-medium truncate">{artifact.name}</p>
        <p className="text-xs text-zinc-500 font-mono truncate">{artifact.file_path}</p>
      </div>
      <div className="text-right text-xs text-zinc-500">
        <p>{formatBytes(artifact.size_bytes)}</p>
        <p>{new Date(artifact.updated_at).toLocaleDateString()}</p>
      </div>
      {artifact.is_committed && (
        <span className="material-icons text-green-500 text-sm" title="Committed to git">
          check_circle
        </span>
      )}
    </div>
  );
}

function DeploymentCard({ deployment }: { deployment: Deployment }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'bg-green-500/20 text-green-400';
      case 'building':
      case 'deploying':
        return 'bg-blue-500/20 text-blue-400';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'failed':
      case 'rolled_back':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'live':
        return 'check_circle';
      case 'building':
      case 'deploying':
        return 'sync';
      case 'pending':
        return 'schedule';
      case 'failed':
        return 'error';
      case 'rolled_back':
        return 'undo';
      default:
        return 'help';
    }
  };

  return (
    <div className="bg-elevated rounded-lg border border-zinc-800 p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className={cn('material-icons', deployment.status === 'live' ? 'text-green-400' : 'text-zinc-500')}>
            {getStatusIcon(deployment.status)}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-zinc-100 font-medium capitalize">{deployment.environment}</span>
              <span className={cn('px-2 py-0.5 text-xs rounded-full', getStatusColor(deployment.status))}>
                {deployment.status}
              </span>
            </div>
            {deployment.version_tag && (
              <p className="text-xs text-zinc-500 font-mono">{deployment.version_tag}</p>
            )}
          </div>
        </div>
        {deployment.deployment_url && (
          <a
            href={deployment.deployment_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm text-brand hover:underline"
          >
            <span className="material-icons text-sm">open_in_new</span>
            View
          </a>
        )}
      </div>

      <div className="flex items-center gap-4 text-xs text-zinc-500">
        <span className="flex items-center gap-1">
          <span className="material-icons text-sm">schedule</span>
          {new Date(deployment.created_at).toLocaleString()}
        </span>
        {deployment.git_commit_sha && (
          <span className="flex items-center gap-1 font-mono">
            <span className="material-icons text-sm">commit</span>
            {deployment.git_commit_sha.slice(0, 8)}
          </span>
        )}
        <span className="flex items-center gap-1">
          <span className="material-icons text-sm">person</span>
          {deployment.initiated_via}
        </span>
      </div>

      {deployment.error_message && (
        <div className="mt-3 p-2 bg-red-500/10 rounded text-xs text-red-400">
          {deployment.error_message}
        </div>
      )}
    </div>
  );
}
